import { useCallback, useEffect, useRef } from 'react';
import { useApi, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';

const POLL_INTERVAL = 10_000;

export default function usePolling({ setMessages, setThinking, onComplete }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const isMounted = useIsMounted();
  const intervalRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(
    (conversationId) => {
      if (!conversationId || intervalRef.current) return;

      intervalRef.current = setInterval(async () => {
        try {
          const { data } = await api.www.workspaces(workspace.id).ai().conversations(conversationId).get();
          if (!isMounted.current) {
            stop();
            return;
          }

          if (data.responseStatus === 'idle') {
            stop();
            setMessages(data.messages);
            setThinking(false);
            onCompleteRef.current?.();
          }
        } catch {
          // Poll failed — keep trying
        }
      }, POLL_INTERVAL);
    },
    [api, workspace.id, isMounted, stop, setMessages, setThinking],
  );

  useEffect(() => () => stop(), [stop]);

  return { start, stop };
}
